import { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ChevronDown, ChevronUp, FileText, WandSparkles } from "lucide-react";

import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import UnsavedChangesBar from "../../../components/UnsavedChangesBar";
import { MarkdownEditor } from "../../../components/MarkdownEditor";
import { AgencyPromptsButton } from "../../../components/prompts/AgencyPromptsButton";
import { brand } from "../../../theme/theme";
import { sectionLabelSx } from "../campaign/campaignUi";
import { PlatformIcon, getPlatformMeta } from "./platformMeta";

const ADAPTATION_PROMPT_STEPS = ["adaptation"];

const cardSx = {
  borderRadius: "16px",
  border: "1px solid #E8ECF2",
  backgroundColor: "#fff",
};

/**
 * Base text of a piece, shown in step 1 (Generación) of the piece page.
 * Without adaptations it also picks the channels and launches "Generar adaptaciones";
 * with adaptations it stays above the network editor for reference and edits.
 */
export function BaseTextPanel({
  value,
  onChange,
  readOnly = false,
  isBusy = false,
  onIterate,
  isIterating = false,
  highlightIterate = false,
  isDirty = false,
  onSave,
  onDiscard,
  isSaving = false,
  channels = null, // platform values to choose from; null hides the channel picker
  selectedChannels = [],
  clientId,
  onToggleChannel,
  onGenerateAdaptations,
  isGeneratingAdaptations = false,
  collapsible = false,
  sx = {},
}) {
  const [collapsed, setCollapsed] = useState(false);
  const editable = !readOnly && !isBusy && !isGeneratingAdaptations;
  const showChannels = Array.isArray(channels);

  return (
    <Box data-tutorial="cpm-raw-editor" sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, minWidth: 34, borderRadius: "10px", background: brand.gradientDiagonal, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FileText size={17} color="#fff" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>Texto base (prompt)</Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
            {showChannels
              ? "Revísalo, elige los canales y genera una adaptación para cada red."
              : "Punto de partida de las adaptaciones a cada red."}
          </Typography>
        </Box>
        {onSave && (
          <UnsavedChangesBar count={isDirty && editable ? 1 : 0} saving={isSaving} onDiscard={onDiscard} onSave={onSave} />
        )}
        {collapsible && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            {collapsed ? "Mostrar" : "Ocultar"}
          </Button>
        )}
      </Box>

      {!collapsed && (
        <Box sx={{ height: showChannels ? { xs: 360, md: 420 } : { xs: 260, md: 300 }, display: "flex", flexDirection: "column" }}>
          <MarkdownEditor
            value={value}
            onChange={onChange}
            minHeight="100%"
            maxHeight="100%"
            placeholder="Edita el texto base (prompt)..."
            readOnly={!editable}
            onIterate={editable && onIterate ? onIterate : undefined}
            isIterating={isIterating}
            iterateTooltip="Regenerar texto base con IA"
            iterateButtonDataTutorial="cpm-iterate-button"
            highlightIterateButton={highlightIterate && editable}
          />
        </Box>
      )}

      {showChannels && (
        <Box>
          <Typography sx={sectionLabelSx}>Adaptaciones a generar</Typography>
          {channels.length === 0 ? (
            <Typography sx={{ fontSize: "0.8125rem", color: "#9CA3AF" }}>
              El cliente no tiene canales configurados. Configúralos en la ficha del cliente.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {channels.map((platform) => {
                const active = selectedChannels.includes(platform);
                return (
                  <Box
                    key={platform}
                    component="button"
                    type="button"
                    disabled={!editable}
                    onClick={() => onToggleChannel?.(platform)}
                    aria-pressed={active}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: "12px",
                      border: `1px solid ${active ? brand.softBorder : "#E5E7EB"}`,
                      backgroundColor: active ? brand.soft : "#fff",
                      color: active ? brand.pinkDark : "#6B7280",
                      font: "inherit",
                      fontSize: "0.8125rem",
                      fontWeight: 700,
                      cursor: editable ? "pointer" : "default",
                      "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
                    }}
                  >
                    <PlatformIcon platform={platform} size={20} sx={{ backgroundColor: "transparent" }} />
                    {getPlatformMeta(platform).label}
                  </Box>
                );
              })}
            </Box>
          )}
          {channels.length > 0 && (
            <AgencyPromptsButton
              steps={ADAPTATION_PROMPT_STEPS}
              platforms={selectedChannels.length ? selectedChannels : channels}
              clientId={clientId}
              hint="La IA adapta cada red con los prompts de tu agencia"
              sx={{ mt: 1.25 }}
            />
          )}
          {channels.length > 0 && (
            <AlertNote variant="info" sx={{ mt: 1.5 }}>
              La programación se hará con <strong>todas las adaptaciones</strong> que generes en este paso:{" "}
              <strong>se publicarán todas a la misma fecha y hora.</strong>
            </AlertNote>
          )}
        </Box>
      )}

      {onGenerateAdaptations && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1.5, borderTop: "1px solid #F1F5F9" }}>
          <Button
            onClick={onGenerateAdaptations}
            disabled={!editable || !String(value || "").trim() || selectedChannels.length === 0}
            sx={{ borderRadius: "999px", background: brand.gradient, "&:hover": { background: brand.gradientHover } }}
          >
            {isGeneratingAdaptations ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <WandSparkles size={15} />}
            {isGeneratingAdaptations ? "Generando adaptaciones..." : "Generar adaptaciones"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
